// Multi-Channel Notification Processor Service
// Handles automatic Discord and WhatsApp notification sending when database notifications are created
// Phase 4 Compliant - Implements IService

import type { Json } from "../../integrations/supabase/types";
import { typedSupabase } from "../../lib/typed-supabase";
import type { Notification, NotificationType } from "../../types/notification";
import { notificationManager } from "./notification-manager.service";
import { channelOrchestrator } from "./channel-orchestrator.service";
import { IService } from "../service-registry";
import { logger } from "../../lib/logger";

export interface IMultiChannelProcessor extends IService {
  processBacklog(): Promise<void>;
  getStatus(): {
    isProcessing: boolean;
    processedCount: number;
    processedNotifications: string[];
  };
  reset(): void;
}

export class MultiChannelProcessor implements IMultiChannelProcessor {
  readonly name = "multiChannelProcessor" as const;
  private isProcessing = false;
  private processedNotifications = new Set<string>();
  private subscription: unknown = undefined;

  async initialize(): Promise<void> {
    if (this.isProcessing) {
      logger.warn("⚠️ MultiChannelProcessor already initialized");
      return;
    }

    // Server-side delivery is authoritative: DB trigger → PGMQ → mycelium-worker
    // + notification-dispatch (QStash). Browser must not send outbound channels.
    this.isProcessing = true;
    logger.info(
      "MultiChannelProcessor: browser delivery disabled; server dispatcher handles multi-channel",
    );
  }

  async dispose(): Promise<void> {
    if (this.subscription) {
      await (this.subscription as { unsubscribe: () => void }).unsubscribe();
      this.subscription = undefined;
    }
    this.processedNotifications.clear();
    this.isProcessing = false;
  }

  /**
   * Initialize real-time processing of multi-channel notifications
   */
  private initializeRealtimeProcessor() {
    // Listen for new notifications that need multi-channel processing
    this.setupRealtimeSubscription();
  }

  private async setupRealtimeSubscription() {
    try {
      logger.info("🔄 Setting up real-time subscription for multi-channel notifications");

      this.subscription = typedSupabase
        .channel("multi-channel-notifications")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: "needs_multi_channel.eq.true",
          },
          (payload) => {
            logger.debug("📨 New multi-channel notification received", { payload });
            this.processNotificationPayload(
              payload.new as Record<string, unknown>
            );
          }
        )
        .subscribe((status) => {
          logger.debug("📡 Multi-channel subscription status", { status });
          if (status === "SUBSCRIBED") {
            logger.info("✅ Multi-channel processor is now listening for notifications");
          }
        });
    } catch (error) {
      logger.error("❌ Failed to setup real-time subscription", { error });
    }
  }

  /**
   * Process a notification payload from realtime
   */
  private async processNotificationPayload(
    notification: Record<string, unknown>
  ) {
    // Basic validation
    if (!notification.id || !notification.user_id || !notification.type) {
      logger.warn("Invalid notification payload", { notification });
      return;
    }

    const notificationId = notification.id as string;
    const userId = notification.user_id as string;
    const type = notification.type as NotificationType;

    // Prevent duplicate processing
    if (this.processedNotifications.has(notificationId)) {
      return;
    }

    this.processedNotifications.add(notificationId);

    try {
      logger.debug(
        `📤 Processing multi-channel notification for user ${userId}`,
        {
          type,
          id: notificationId,
        }
      );

      // Get the full notification from database to ensure we have all the data
      const { data: fullNotification, error } = await typedSupabase
        .from("notifications")
        .select("*")
        .eq("id", notificationId)
        .single();

      if (error || !fullNotification) {
        logger.error("Failed to fetch full notification", { error, notificationId });
        this.processedNotifications.delete(notificationId);
        return;
      }

      // Use the channel orchestrator to send to Discord and push channels
      const result = await channelOrchestrator.deliverToAllChannels(fullNotification as unknown as Notification);

      logger.info(`✅ Multi-channel notification processed`, {
        notification_id: notificationId,
        success: result.success,
        status: result.success ? "success" : "error",
        message: result.success ? "Notification sent successfully" : result.error,
      });
    } catch (error) {
      logger.error(
        `❌ Error processing multi-channel notification ${notificationId}`,
        { error }
      );

      // Remove from processed set so it can be retried
      this.processedNotifications.delete(notificationId);
    }
  }

  /**
   * Get appropriate title for notification type
   */
  private getNotificationTitle(type: NotificationType): string {
    const titleMap: Record<string, string> = {
      TASK_ASSIGNED: "📋 New Task Assignment",
      TASK_COMPLETED: "✅ Task Completed",
      TASK_STATUS_CHANGED: "🔄 Task Status Update",
      TASK_DUE_REMINDER: "⏰ Task Due Reminder",
      TASK_OVERDUE: "🚨 Overdue Task Alert",
      TASK_COMMENT: "💬 New Task Comment",
      TASK_MENTION: "👋 You were mentioned",
      TASK_PRIORITY_CHANGED: "⚡ Priority Changed",
      TASK_ATTACHMENT_ADDED: "📎 File Attached",
      SUBTASK_COMPLETED: "✅ Subtask Completed",
      POST_LIKED: "❤️ Post Liked",
      POST_COMMENTED: "💬 New Comment",
      COMMENT_REPLIED: "↩️ Comment Reply",
      COMMUNITY_MENTION: "👋 Community Mention",
      ROLE_CHANGED: "🔐 Role Changed",
      SYSTEM_ANNOUNCEMENT: "📢 System Announcement",
      ADMIN_ACTION: "🛠️ Admin Action",
      RIZZ_MILESTONE: "🎉 XP Milestone!", // deprecated — use XP_MILESTONE
      XP_MILESTONE: "🎉 XP Milestone!",
      STREAK_BONUS: "🔥 Streak Bonus!",
      ACHIEVEMENT_BADGE: "🏆 Achievement Unlocked!",
      LEADERBOARD_UPDATE: "📊 Leaderboard Update",
    };

    return titleMap[type] || "🔔 Notification";
  }

  /**
   * Manually process notifications that need multi-channel delivery
   * Useful for processing notifications that were created before the processor was running
   */
  async processBacklog() {
    if (this.isProcessing) {
      logger.debug("Already processing backlog, skipping");
      return;
    }

    this.isProcessing = true;

    try {
      logger.info("🔍 Processing multi-channel notification backlog");

      // Get notifications that need multi-channel processing but haven't been processed
      const { data: notifications, error } = await typedSupabase
        .from("notifications")
        .select("*")
        .eq("needs_multi_channel", true)
        .is("data->multi_channel_processed", null) // Must use null for SQL IS NULL
        .order("created_at", { ascending: true })
        .limit(50);

      if (error) {
        throw error;
      }

      if (!notifications || notifications.length === 0) {
        logger.debug("No backlog notifications to process");
        return;
      }

      logger.info(
        `📦 Found ${notifications.length} backlog notifications to process`
      );

      // Process each notification with a small delay to avoid overwhelming services
      for (const notification of notifications) {
        await this.processNotificationPayload(notification);
        // Small delay between notifications to be respectful to Discord/WhatsApp APIs
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      logger.info("✅ Backlog processing completed");
    } catch (error) {
      logger.error("❌ Error processing notification backlog", { error });
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Get processor status
   */
  getStatus() {
    return {
      isProcessing: this.isProcessing,
      processedCount: this.processedNotifications.size,
      processedNotifications: Array.from(this.processedNotifications),
    };
  }

  /**
   * Reset processed notifications cache (useful for testing)
   */
  reset() {
    this.processedNotifications.clear();
    logger.debug("🔄 Multi-channel processor reset");
  }
}

// Create singleton instance
export const multiChannelProcessor = new MultiChannelProcessor();

// Export for testing and manual control
export default multiChannelProcessor;

